import { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Plus, Search, Trash2, Pencil, ChevronDown, ChevronRight, ListChecks, RefreshCw, Calendar } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

// Activity form component - defined OUTSIDE Services to prevent remount on state change
const ActivityFormFields = ({ form, setForm }) => (
  <div className="space-y-4">
    <div>
      <Label>Activity Name <span className="text-red-600">*</span></Label>
      <Input
        data-testid="activity-name-input"
        placeholder="e.g., GST 2A Reconciliation"
        value={form.name}
        onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
        required
      />
    </div>
    <div>
      <Label>Description</Label>
      <Textarea
        data-testid="activity-description-input"
        placeholder="Brief description of this activity"
        value={form.description}
        onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
        rows={2}
      />
    </div>
    <div className="flex items-center gap-3">
      <Switch
        data-testid="activity-recurring-switch"
        checked={form.is_recurring}
        onCheckedChange={(checked) => setForm(prev => ({ ...prev, is_recurring: checked }))}
      />
      <Label className="cursor-pointer">Recurring Activity</Label>
    </div>
    {form.is_recurring && (
      <div className="space-y-3 p-3 bg-slate-50 rounded-lg border">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Frequency</Label>
            <Select value={form.recurrence_type} onValueChange={(v) => setForm(prev => ({ ...prev, recurrence_type: v, due_weekday: '', due_month: '', special_rule: '' }))}>
              <SelectTrigger data-testid="activity-frequency-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="bi_monthly">Bi-Monthly</SelectItem>
                <SelectItem value="quarterly">Quarterly</SelectItem>
                <SelectItem value="yearly">Yearly</SelectItem>
                <SelectItem value="one_time">One-Time</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {['monthly', 'bi_monthly', 'quarterly', 'yearly'].includes(form.recurrence_type) && (
            <div>
              <Label>Due Day of Month</Label>
              <Input data-testid="activity-deadline-day-input" type="number" min="1" max="31" placeholder="e.g., 14"
                value={form.default_deadline_day} onChange={(e) => setForm(prev => ({ ...prev, default_deadline_day: e.target.value }))} />
            </div>
          )}
        </div>
        {form.recurrence_type === 'weekly' && (
          <div>
            <Label>Due Weekday</Label>
            <Select value={form.due_weekday || ''} onValueChange={(v) => setForm(prev => ({ ...prev, due_weekday: v }))}>
              <SelectTrigger data-testid="activity-weekday-select"><SelectValue placeholder="Select day" /></SelectTrigger>
              <SelectContent>
                {['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(d => (
                  <SelectItem key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {form.recurrence_type === 'yearly' && (
          <div>
            <Label>Due Month</Label>
            <Select value={String(form.due_month || '')} onValueChange={(v) => setForm(prev => ({ ...prev, due_month: v }))}>
              <SelectTrigger data-testid="activity-month-select"><SelectValue placeholder="Select month" /></SelectTrigger>
              <SelectContent>
                {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                  <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {form.recurrence_type === 'quarterly' && (
          <div>
            <Label>Special Rule</Label>
            <Select value={form.special_rule || 'none'} onValueChange={(v) => setForm(prev => ({ ...prev, special_rule: v === 'none' ? '' : v }))}>
              <SelectTrigger data-testid="activity-special-rule-select"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="quarter_end">Quarter End</SelectItem>
                <SelectItem value="quarter_start">Quarter Start</SelectItem>
                <SelectItem value="last_day_quarter">Last Day of Quarter</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    )}
  </div>
);

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedService, setExpandedService] = useState(null);
  const [activities, setActivities] = useState({});

  // Service dialogs
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [editFormData, setEditFormData] = useState({ name: '', description: '' });

  // Activity dialogs
  const [isActivityDialogOpen, setIsActivityDialogOpen] = useState(false);
  const [isEditActivityDialogOpen, setIsEditActivityDialogOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [activityForm, setActivityForm] = useState({
    name: '', description: '', is_recurring: true, recurrence_type: 'monthly', default_deadline_day: '', due_weekday: '', due_month: '', special_rule: ''
  });
  const [editActivityForm, setEditActivityForm] = useState({
    name: '', description: '', is_recurring: true, recurrence_type: 'monthly', default_deadline_day: '', due_weekday: '', due_month: '', special_rule: ''
  });

  useEffect(() => { fetchServices(); }, []);

  const fetchServices = async () => {
    try {
      const response = await api.get('/services');
      setServices(response.data);
    } catch (error) {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivities = async (serviceId) => {
    try {
      const response = await api.get(`/services/${serviceId}/activities`);
      setActivities(prev => ({ ...prev, [serviceId]: response.data }));
    } catch (error) {
      toast.error('Failed to load activities');
    }
  };

  const toggleExpand = (serviceId) => {
    if (expandedService === serviceId) {
      setExpandedService(null);
    } else {
      setExpandedService(serviceId);
      if (!activities[serviceId]) {
        fetchActivities(serviceId);
      }
    }
  };

  // Service CRUD
  const handleCreateService = async (e) => {
    e.preventDefault();
    try {
      await api.post('/services', formData);
      toast.success('Service created successfully');
      setIsCreateDialogOpen(false);
      setFormData({ name: '', description: '' });
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create service');
    }
  };

  const handleEditService = (service) => {
    setSelectedService(service);
    setEditFormData({ name: service.name, description: service.description || '' });
    setIsEditDialogOpen(true);
  };

  const handleUpdateService = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/services/${selectedService.id}`, editFormData);
      toast.success('Service updated successfully');
      setIsEditDialogOpen(false);
      setSelectedService(null);
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update service');
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('Delete this service and all its activities?')) return;
    try {
      await api.delete(`/services/${serviceId}`);
      toast.success('Service deleted');
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete service');
    }
  };

  // Activity CRUD
  const openAddActivity = (serviceId) => {
    setSelectedService(services.find(s => s.id === serviceId));
    setActivityForm({ name: '', description: '', is_recurring: true, recurrence_type: 'monthly', default_deadline_day: '', due_weekday: '', due_month: '', special_rule: '' });
    setIsActivityDialogOpen(true);
  };

  const handleCreateActivity = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...activityForm };
      payload.default_deadline_day = payload.default_deadline_day ? parseInt(payload.default_deadline_day) : null;
      payload.due_month = payload.due_month ? parseInt(payload.due_month) : null;
      payload.due_weekday = payload.due_weekday || null;
      payload.special_rule = payload.special_rule || null;
      await api.post(`/services/${selectedService.id}/activities`, payload);
      toast.success('Activity added');
      setIsActivityDialogOpen(false);
      fetchActivities(selectedService.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add activity');
    }
  };

  const handleEditActivity = (activity) => {
    setSelectedActivity(activity);
    setEditActivityForm({
      name: activity.name,
      description: activity.description || '',
      is_recurring: activity.is_recurring,
      recurrence_type: activity.recurrence_type || 'monthly',
      default_deadline_day: activity.default_deadline_day || '',
      due_weekday: activity.due_weekday || '',
      due_month: activity.due_month ? String(activity.due_month) : '',
      special_rule: activity.special_rule || ''
    });
    setIsEditActivityDialogOpen(true);
  };

  const handleUpdateActivity = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editActivityForm };
      payload.default_deadline_day = payload.default_deadline_day ? parseInt(payload.default_deadline_day) : null;
      payload.due_month = payload.due_month ? parseInt(payload.due_month) : null;
      payload.due_weekday = payload.due_weekday || null;
      payload.special_rule = payload.special_rule || null;
      await api.put(`/activities/${selectedActivity.id}`, payload);
      toast.success('Activity updated');
      setIsEditActivityDialogOpen(false);
      fetchActivities(selectedActivity.service_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update activity');
    }
  };

  const handleDeleteActivity = async (activity) => {
    if (!window.confirm(`Delete activity "${activity.name}"?`)) return;
    try {
      await api.delete(`/activities/${activity.id}`);
      toast.success('Activity deleted');
      fetchActivities(activity.service_id);
    } catch (error) {
      toast.error('Failed to delete activity');
    }
  };

  const filteredServices = services.filter(service =>
    service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (service.description && service.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8 min-h-full" style={{
        background: 'linear-gradient(135deg, rgba(91, 111, 214, 0.03) 0%, rgba(16, 185, 129, 0.03) 100%)'
      }}>
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold tracking-tight mb-2" style={{
              background: 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>Services & Activities</h1>
            <p className="text-lg text-slate-600">Manage service heads and link activities to each service</p>
          </div>
          <Button
            onClick={() => setIsCreateDialogOpen(true)}
            className="bg-gradient-to-r from-[#5B6FD6] to-[#10B981] hover:from-[#4a5bc5] hover:to-[#0d9668] shadow-md"
            data-testid="create-service-button"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Service
          </Button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              placeholder="Search services..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white/80"
              data-testid="search-services-input"
            />
          </div>
        </div>

        {/* Services List with Expandable Activities */}
        {filteredServices.length === 0 ? (
          <Card className="border-slate-200 shadow-sm bg-white/80 backdrop-blur-sm">
            <CardContent className="text-center py-12">
              <ListChecks className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 text-lg">No services found</p>
              <p className="text-slate-400 text-sm mt-2">Click "Add Service" to create your first service</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredServices.map((service) => {
              const isExpanded = expandedService === service.id;
              const serviceActivities = activities[service.id] || [];

              return (
                <Card key={service.id} className="border-slate-200 shadow-sm bg-white/80 backdrop-blur-sm overflow-hidden" data-testid={`service-card-${service.id}`}>
                  {/* Service Row */}
                  <div
                    className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => toggleExpand(service.id)}
                    data-testid={`service-expand-${service.id}`}
                  >
                    <div className="flex items-center gap-4">
                      {isExpanded
                        ? <ChevronDown className="h-5 w-5 text-[#5B6FD6]" />
                        : <ChevronRight className="h-5 w-5 text-slate-400" />
                      }
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900">{service.name}</h3>
                        {service.description && (
                          <p className="text-sm text-slate-500 mt-0.5">{service.description}</p>
                        )}
                      </div>
                      {isExpanded && serviceActivities.length > 0 && (
                        <Badge className="bg-[#5B6FD6]/10 text-[#5B6FD6] ml-2">
                          {serviceActivities.length} {serviceActivities.length === 1 ? 'activity' : 'activities'}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditService(service)}
                        className="text-slate-600 hover:text-[#5B6FD6] hover:bg-[#5B6FD6]/10"
                        data-testid={`edit-service-${service.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteService(service.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-service-${service.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Expanded Activities Section */}
                  {isExpanded && (
                    <div className="border-t border-slate-100 bg-slate-50/50 px-6 py-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                          <ListChecks className="h-4 w-4 text-[#10B981]" />
                          Activities under {service.name}
                        </h4>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openAddActivity(service.id)}
                          className="text-xs border-[#5B6FD6]/30 text-[#5B6FD6] hover:bg-[#5B6FD6]/10"
                          data-testid={`add-activity-${service.id}`}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add Activity
                        </Button>
                      </div>

                      {serviceActivities.length === 0 ? (
                        <p className="text-sm text-slate-400 py-4 text-center">No activities yet. Add activities to define recurring tasks for this service.</p>
                      ) : (
                        <div className="space-y-2">
                          {serviceActivities.map((activity) => (
                            <div
                              key={activity.id}
                              className="flex items-center justify-between bg-white rounded-lg border border-slate-200 px-4 py-3 hover:shadow-sm transition-shadow"
                              data-testid={`activity-row-${activity.id}`}
                            >
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-slate-900 text-sm">{activity.name}</span>
                                  {activity.is_recurring && (
                                    <Badge variant="outline" className="text-xs flex items-center gap-1 text-[#10B981] border-[#10B981]/30">
                                      <RefreshCw className="h-3 w-3" />
                                      {(activity.recurrence_type || '').replace('_', '-')}
                                    </Badge>
                                  )}
                                  {!activity.is_recurring && (
                                    <Badge variant="outline" className="text-xs text-slate-400">One-time</Badge>
                                  )}
                                  {activity.default_deadline_day && (
                                    <Badge variant="outline" className="text-xs flex items-center gap-1 text-slate-500">
                                      <Calendar className="h-3 w-3" />
                                      Day {activity.default_deadline_day}
                                    </Badge>
                                  )}
                                  {activity.due_weekday && (
                                    <Badge variant="outline" className="text-xs text-slate-500 capitalize">
                                      {activity.due_weekday}
                                    </Badge>
                                  )}
                                  {activity.due_month && (
                                    <Badge variant="outline" className="text-xs text-slate-500">
                                      Month {activity.due_month}
                                    </Badge>
                                  )}
                                  {activity.special_rule && (
                                    <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                                      {(activity.special_rule || '').replace(/_/g, ' ')}
                                    </Badge>
                                  )}
                                </div>
                                {activity.description && (
                                  <p className="text-xs text-slate-500 mt-1">{activity.description}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-1 ml-4">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditActivity(activity)}
                                  className="h-8 w-8 p-0 text-slate-500 hover:text-[#5B6FD6]"
                                  data-testid={`edit-activity-${activity.id}`}
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteActivity(activity)}
                                  className="h-8 w-8 p-0 text-red-400 hover:text-red-600"
                                  data-testid={`delete-activity-${activity.id}`}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        <div className="mt-6 text-sm text-slate-600">
          Total Services: <span className="font-semibold text-slate-900">{filteredServices.length}</span>
        </div>

        {/* Create Service Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent data-testid="create-service-dialog">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">Add New Service</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateService} className="space-y-4">
              <div>
                <Label>Service Name <span className="text-red-600">*</span></Label>
                <Input
                  data-testid="service-name-input"
                  placeholder="e.g., GST, TDS, Payroll"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  data-testid="service-description-input"
                  placeholder="Brief description of the service"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-gradient-to-r from-[#5B6FD6] to-[#10B981]" data-testid="service-submit-button"
                  disabled={!formData.name.trim()}>
                  Add Service
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Service Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent data-testid="edit-service-dialog">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">Edit Service</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateService} className="space-y-4">
              <div>
                <Label>Service Name <span className="text-red-600">*</span></Label>
                <Input
                  data-testid="edit-service-name-input"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  data-testid="edit-service-description-input"
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  rows={3}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-gradient-to-r from-[#5B6FD6] to-[#10B981]" data-testid="edit-service-submit-button"
                  disabled={!editFormData.name.trim()}>
                  Update Service
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Create Activity Dialog */}
        <Dialog open={isActivityDialogOpen} onOpenChange={setIsActivityDialogOpen}>
          <DialogContent data-testid="create-activity-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">
                Add Activity to <span className="text-[#5B6FD6]">{selectedService?.name}</span>
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateActivity}>
              <ActivityFormFields form={activityForm} setForm={setActivityForm} />
              <DialogFooter className="mt-6">
                <Button type="button" variant="outline" onClick={() => setIsActivityDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-gradient-to-r from-[#5B6FD6] to-[#10B981]" data-testid="activity-submit-button"
                  disabled={!activityForm.name.trim()}>
                  Add Activity
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Activity Dialog */}
        <Dialog open={isEditActivityDialogOpen} onOpenChange={setIsEditActivityDialogOpen}>
          <DialogContent data-testid="edit-activity-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">Edit Activity</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateActivity}>
              <ActivityFormFields form={editActivityForm} setForm={setEditActivityForm} />
              <DialogFooter className="mt-6">
                <Button type="button" variant="outline" onClick={() => setIsEditActivityDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-gradient-to-r from-[#5B6FD6] to-[#10B981]" data-testid="edit-activity-submit-button"
                  disabled={!editActivityForm.name.trim()}>
                  Update Activity
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
    </div>
  );
}
